import { useEffect, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useNetworkStatus } from "../../Hooks/useNetworkStatus";

export default function OfflineBanner() {
  const { isOnline, speed } = useNetworkStatus();
  const [wasOffline, setWasOffline] = useState(false);
  const [showRestored, setShowRestored] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
      setShowRestored(false);
      setDismissed(false);
    } else if (wasOffline) {
      setShowRestored(true);
      setDismissed(false);
      const t = setTimeout(() => {
        setShowRestored(false);
        setWasOffline(false);
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [isOnline, wasOffline]);

  const isSlowConnection = speed === "slow" && isOnline && !showRestored;
  const visible = !isOnline || showRestored || (speed === "slow");
  if (!visible) return null;

  const getBannerStyles = () => {
    if (!isOnline) {
      return { backgroundColor: "#d32f2f", color: "#fff", dotColor: "#ef9a9a" };
    }
    if (showRestored) {
      return { backgroundColor: "#2e7d32", color: "#fff", dotColor: "#a5d6a7" };
    }
    return { backgroundColor: "#ff9800", color: "#fff", dotColor: "#ffe0b2" };
  };

  const { backgroundColor, color, dotColor } = getBannerStyles();

  const getMessage = () => {
    if (!isOnline) return "You are offline. Please check your internet connection.";
    if (showRestored) return "Connection restored.";
    return "Slow connection detected. Loading may take longer than usual.";
  };

  // When dismissed, show only a small dot at the top-right corner
  if (dismissed && isSlowConnection) {
    return (
      <Box
        onClick={() => setDismissed(false)}
        sx={{
          position: "fixed",
          top: 8,
          right: 8,
          zIndex: 9999,
          width: 12,
          height: 12,
          borderRadius: "50%",
          backgroundColor: "#ff9800",
          cursor: "pointer",
          animation: "pulse 1.5s infinite",
          "@keyframes pulse": {
            "0%, 100%": { opacity: 1 },
            "50%": { opacity: 0.4 },
          },
        }}
        title="Slow connection – click to expand"
      />
    );
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        px: 2,
        py: 1,
        backgroundColor,
        color,
        transition: "all 0.3s ease",
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: dotColor,
          flexShrink: 0,
          ...(isSlowConnection
            ? { animation: "pulse 1.5s infinite" }
            : {}),
          "@keyframes pulse": {
            "0%, 100%": { opacity: 1 },
            "50%": { opacity: 0.4 },
          },
        }}
      />
      <Typography variant="body2" fontWeight={500}>
        {getMessage()}
      </Typography>
      {isSlowConnection && (
        <IconButton
          size="small"
          onClick={() => setDismissed(true)}
          sx={{ color, p: 0.25, ml: 0.5 }}
          aria-label="Dismiss"
        >
          <KeyboardArrowUpIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}
